import { Chess } from 'chessops/chess';
import { makeFen, parseFen } from 'chessops/fen';
import { makeSan, makeSanAndPlay } from 'chessops/san';
import { parseUci } from 'chessops/util';

/** Notation only. Legal destinations and committed moves still come from the API. */
export function sanLabel(fen: string, uci: string): string {
  try {
    const position = Chess.fromSetup(parseFen(fen).unwrap()).unwrap();
    const move = parseUci(uci);
    return move ? makeSan(position, move) : 'Move';
  } catch {
    return 'Move';
  }
}

/**
 * SAN for an engine principal variation, up to `limit` moves. Stops at the first
 * move it cannot read, so a malformed PV shortens rather than garbles the line.
 */
export function pvSan(fen: string, pv: readonly string[], limit = 8): string[] {
  const out: string[] = [];
  try {
    const position = Chess.fromSetup(parseFen(fen).unwrap()).unwrap();
    for (const uci of pv.slice(0, limit)) {
      const move = parseUci(uci);
      if (!move || !position.isLegal(move)) break;
      out.push(makeSanAndPlay(position, move));
    }
  } catch {
    // Unreadable FEN: no line rather than a wrong one.
  }
  return out;
}

/**
 * Replay a server-validated game for display: SAN per ply and the FEN before each
 * ply (`fens[i]` is the position before move `i`; `fens[moves.length]` is final).
 * Stops at the first unreadable move, so a bad record shortens rather than garbles.
 */
export function replayLine(
  fen: string,
  moves: readonly string[],
): { sans: string[]; fens: string[] } {
  const sans: string[] = [];
  const fens: string[] = [fen];
  const setup = parseFen(fen);
  const parsed = setup.isOk ? Chess.fromSetup(setup.value) : null;
  // Unreadable start position: show no moves rather than wrong ones. Anything else
  // that throws is a real bug and must surface, not silently shorten the game.
  if (parsed === null || !parsed.isOk) return { sans, fens };
  const position = parsed.value;
  for (const uci of moves) {
    const move = parseUci(uci);
    if (!move || !position.isLegal(move)) break;
    sans.push(makeSanAndPlay(position, move));
    fens.push(makeFen(position.toSetup()));
  }
  return { sans, fens };
}
